<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import GaiaIconButton from '../base/GaiaIconButton.vue';
import GaiaStatusChip, { type GaiaChipTone } from '../base/GaiaStatusChip.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  /** Second line, e.g. "Signer · Electronic signature · Kova AG". */
  detail: {
    type: String,
    default: undefined,
  },
  /** Third line, e.g. "Reminded 12 Oct 2026, 14:03". */
  reminder: {
    type: String,
    default: undefined,
  },
  initials: {
    type: String,
    default: '',
  },
  /** Participant state — adapt ParticipantStatus via statusMaps.participantStatusMap. */
  tone: {
    type: String as PropType<GaiaChipTone>,
    default: 'neutral',
  },
  stateLabel: {
    type: String,
    required: true,
  },
  /** Offer the reminder button — only meaningful while they still owe a signature. */
  canRemind: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ remind: [] }>();

const divider = gaiaNeutral.borderSoft;
</script>

<template>
  <!-- One participant on the signature-request detail: avatar, name + detail,
       their state chip, and the reminder button when one is still owed. -->
  <n-flex
    align="center"
    :size="11"
    :wrap="false"
    :style="{ padding: '12px 18px', borderBottom: `1px solid ${divider}` }"
  >
    <slot name="avatar">
      <gaia-avatar :size="30">{{ props.initials }}</gaia-avatar>
    </slot>
    <n-flex vertical :size="2" :style="{ minWidth: 0 }">
      <gaia-text truncate size="body" weight="semibold" tone="ink">
        {{ props.name }}
      </gaia-text>
      <gaia-text v-if="props.detail" truncate size="meta" tone="faint">
        {{ props.detail }}
      </gaia-text>
      <gaia-text v-if="props.reminder" truncate size="meta" tone="faint">
        {{ props.reminder }}
      </gaia-text>
    </n-flex>
    <n-flex align="center" :size="8" :wrap="false" :style="{ marginLeft: 'auto', flex: 'none' }">
      <gaia-status-chip :tone="props.tone">{{ props.stateLabel }}</gaia-status-chip>
      <gaia-icon-button
        v-if="props.canRemind"
        :dimension="26"
        title="Send reminder"
        :aria-label="`Send ${props.name} a reminder`"
        @click="emit('remind')"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M2 4.5h12v8H2z" />
          <path d="m2.4 5 5.6 4 5.6-4" />
        </svg>
      </gaia-icon-button>
    </n-flex>
  </n-flex>
</template>
